/**
 * WizardSection - Styling components for Settings Wizard
 * Extracted from proven wizard reference - ensures consistent UX
 * GPT Blueprint: Commission-worthy polish with mobile optimization
 */

import React from 'react';

// Main container - full width mobile, desktop preserved
const WizardSection = ({ children, className = '' }) => {
  return (
    <div className={`px-2 space-y-4 md:px-0 md:space-y-3 ${className}`}>
      {children}
    </div>
  );
};

// Standard card wrapper - mobile optimized padding, desktop preserved
const Card = ({ children, className = '', variant = 'default' }) => {
  const baseClasses = 'rounded-xl border bg-white shadow-sm';
  const variants = {
    default: 'border-gray-200 p-4 md:p-3',
    muted: 'border-gray-200 bg-gray-50 p-4 md:p-4',
    warning: 'border-amber-300 bg-amber-50 p-4 md:p-3',
    info: 'border-blue-200 bg-blue-50 p-4 md:p-4'
  };

  return (
    <div className={`${baseClasses} ${variants[variant]} ${className}`}>
      {children}
    </div>
  );
};

// Section header with mobile-friendly typography, desktop preserved
const Header = ({ children, className = '', level = 'h3' }) => {
  const baseClasses = 'font-semibold tracking-wide uppercase mb-2';
  const levels = {
    h1: 'text-xl md:text-[22px] tracking-[-0.01em] text-gray-900',
    h2: 'text-lg md:text-[16px] text-gray-700',
    h3: 'text-base md:text-sm text-gray-600'
  };

  const Component = level;
  
  return (
    <Component className={`${baseClasses} ${levels[level]} ${className}`}>
      {children}
    </Component>
  );
};

// Body text with mobile readability, desktop preserved
const Text = ({ children, className = '', size = 'default' }) => {
  const sizes = {
    default: 'text-base md:text-[15px] text-gray-700',
    body: 'text-base md:text-[14px] leading-[1.6] text-gray-700',
    small: 'text-sm md:text-[12px] leading-[1.5] text-gray-500'
  };

  return (
    <p className={`${sizes[size]} ${className}`}>
      {children}
    </p>
  );
};

// Callout/Alert component - mobile optimized, desktop preserved
const Callout = ({ children, variant = 'info', title, className = '' }) => {
  const variants = {
    info: {
      container: 'border-blue-200 bg-blue-50',
      title: 'text-base md:text-[14px] font-semibold text-blue-800',
      content: 'text-base md:text-[14px] text-blue-900/90'
    },
    warning: {
      container: 'border-amber-300 bg-amber-50', 
      title: 'text-base md:text-[14px] font-semibold text-amber-800',
      content: 'text-base md:text-[14px] text-amber-900/90'
    },
    success: {
      container: 'border-green-200 bg-green-50',
      title: 'text-base md:text-[14px] font-semibold text-green-800', 
      content: 'text-base md:text-[14px] text-green-900/90'
    },
    regulatory: {
      container: 'border-purple-200 bg-purple-50',
      title: 'text-base md:text-[14px] font-semibold text-purple-800',
      content: 'text-base md:text-[14px] text-purple-900/90'
    }
  };

  const variantStyles = variants[variant] || variants.info;

  return (
    <div className={`rounded-xl border p-4 md:p-4 ${variantStyles.container} ${className}`}>
      {title && (
        <h3 className={variantStyles.title}>{title}</h3>
      )}
      <div className={`${title ? 'mt-2' : ''} ${variantStyles.content}`}>
        {children}
      </div>
    </div>
  );
};

// Form field wrapper with mobile text sizing, desktop preserved
const Field = ({ label, help, error, children, className = '' }) => {
  return (
    <div className={`space-y-1.5 ${className}`}>
      {label && (
        <label className="block text-sm md:text-[12px] font-medium text-gray-600">
          {label}
        </label>
      )}
      {children}
      {help && (
        <p className="text-sm md:text-[12px] text-gray-500">{help}</p>
      )}
      {error && (
        <p className="text-sm md:text-[12px] text-red-600">{error}</p>
      )}
    </div>
  );
};

// Standard input component with mobile touch targets, desktop preserved
const Input = ({ className = '', ...props }) => {
  return (
    <input
      className={`w-full rounded-lg border border-gray-300 bg-white px-3 py-3 md:py-2.5 text-base md:text-[14px] text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-500 ${className}`}
      {...props}
    />
  );
};

// Select component with mobile touch targets
const Select = ({ className = '', children, ...props }) => {
  return (
    <select
      className={`w-full rounded-lg border border-gray-300 bg-white px-3 py-3 md:py-2.5 text-base md:text-[14px] text-gray-900 focus:outline-none focus:ring-2 focus:ring-cyan-500 ${className}`}
      {...props}
    >
      {children}
    </select>
  );
};

// Button components with mobile touch targets, desktop preserved
const Button = ({ variant = 'primary', size = 'default', className = '', children, ...props }) => {
  const baseClasses = 'inline-flex items-center justify-center rounded-lg font-medium shadow-sm disabled:opacity-50 transition-colors';
  
  const variants = {
    primary: 'bg-cyan-600 text-white hover:bg-cyan-700',
    secondary: 'border border-gray-200 bg-white text-gray-700 hover:bg-gray-50',
    ghost: 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
  };

  const sizes = {
    default: 'px-4 py-3 md:py-2.5 text-base md:text-[14px]',
    small: 'px-3 py-2.5 md:py-2 text-sm md:text-[13px]',
    large: 'px-6 py-4 md:py-3 text-lg md:text-[16px]'
  };

  return (
    <button
      className={`${baseClasses} ${variants[variant]} ${sizes[size]} ${className}`}
      {...props}
    >
      {children}
    </button>
  );
};

// Export all components as properties
WizardSection.Card = Card;
WizardSection.Header = Header;
WizardSection.Text = Text;
WizardSection.Callout = Callout;
WizardSection.Field = Field;
WizardSection.Input = Input;
WizardSection.Select = Select;
WizardSection.Button = Button;

export default WizardSection;